import React, { useState, useEffect } from 'react';
import { getInquiries } from './Inquiries';
import { useBSToasts } from '../Shared/BSToasts';
import { globalErrorHandler } from '../../api/create-api';
import { Row, Col, Table } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRedoAlt, faTimes } from '@fortawesome/free-solid-svg-icons';
import { SearchBar } from '@galop/component-library';
import { ViewInquiry } from './ViewInquiry';
import { FuzzyDate } from '../../Helpers/Helper';

export const InquiryList = (props) => {
    const { config, CurrentUser, resourceIsDirty, setResourceIsDirty } = props;
    const [toasts, addToast, removeToast] = useBSToasts([]);
    const [loading, setLoading] = useState(false);
    const [viewResource, setViewResource] = useState(null);
    const [resources, setResources] = useState(props.resources || []);
    const [filteredResources, setFilteredResources] = useState([]);
    const [filtered, setFiltered] = useState('');

    useEffect(() => {
        if (CurrentUser) {
            getResources(false);
        }
    }, [CurrentUser]);

    useEffect(() => {
        filterResources(resources);
    }, [filtered, resources]);

    useEffect(() => {
        if (viewResource && !!resources.find(resource => resource.id === viewResource.id)) {
            setViewResource(() => (resources.find(resource => resource.id === viewResource.id)));
        }
    }, [resources]);

    const getResources = (loadFullObjects) => {
        getInquiries((loadFullObjects) => {
            if (!loadFullObjects) {
                addToast(`Loading ${config.pluralCapitalResourceName()}`, { appearance: 'info', id: 'loading-resources' });
            }
        }, loadFullObjects, (data) => {
            removeToast('loading-resources', () => {
                var loadedData = data.map(resource => ({...resource, loaded: loadFullObjects}))
                setResources(loadedData);
                setFilteredResources(loadedData);
                setLoading(false);
            });
        }, error => globalErrorHandler(error, addToast));
    };

    const filterResources = (unfiltered) => {
        let filteredList = [];

        if (filtered !== "") {
            filteredList = unfiltered.filter(resource => {
                const lcFilter = filtered.toLowerCase();

                // Search by table columns
                const name = (resource.name || '').toLowerCase(); // Name
                const property = (resource.propertyName || '').toLowerCase(); // Property Name
                const submitted = new Date(resource.submitted).toLocaleDateString('en-US'); // Date Submitted


                return name.includes(lcFilter) ||
                    property.includes(lcFilter) ||
                    submitted.includes(lcFilter);
            });
        }
        else {
            filteredList = unfiltered;
        }

        setFilteredResources(filteredList);
    };

    const changeViewResource = (resource) => {
        if (!resourceIsDirty) {
            setViewResource((viewResource || {}).id !== resource.id ? resource : null);
        }
    };

    const updateResource = (resource) => {
        const newResources = resources;
        if (resource && typeof resource === 'object') { /* Save */
            const resourceInList = resources.find(ast => ast.id === resource.id);
            const index = resources.indexOf(resourceInList);

            if (index > -1) {
                newResources[index] = resource;
            }
            else {
                newResources.push(resource);
            }
        }
        else { /* Delete */
            const resourceInList = resources.find(ast => ast.id === resource);
            const index = resources.indexOf(resourceInList);

            newResources.splice(index, 1);
        }
        setResources([...newResources]);
    };

    return (!loading &&
        <div className="flex-fill h-75 pb-5 pb-sm-3">
            <Row className="h-100">
                <Col md="4" lg="3" className={`h-100 flex-column ${viewResource ? 'd-none d-md-flex' : 'd-flex '}`}>
                    <div className={`py-1 ${viewResource ? 'd-none d-md-block' : ''}`}>
                        <div>
                            <SearchBar value={filtered} onChange={(value) => setFiltered(value)} placeHolder={`Search ${config.pluralCapitalResourceName()}`} Reset={() => setFiltered('')} />
                            {config.showHelp &&
                                <p className="mt-2">Use this screen to view / edit {config.pluralResourceName()} in this system.</p>
                            }
                        </div>
                    </div>
                    {config.listHeader &&
                        <div className={`text-dark align-middle mb-1 ${viewResource ? 'd-none d-md-block' : ''}`}>
                            <span className="h4">{config.listHeader}<FontAwesomeIcon className="fs-6 ms-1 text-secondary" icon={faRedoAlt} onClick={() => getResources(false)} /></span>
                        </div>
                    }
                    <div className={`flex-fill border rounded scrollable-list ${viewResource ? 'd-none d-md-block' : ''}`} style={{ overflowX: 'hidden', overflowY: 'auto' }}>
                        <Table hover={!resourceIsDirty} className={`mb-0`}>
                            <tbody>
                                {(filteredResources || []).map(resource => (
                                    <tr key={resource.id || Math.random()} className={`${resourceIsDirty ? '' : 'cursor-pointer'} ${resource.id === (viewResource || {}).id ? 'bg-secondary text-white' : resource.deleted ? 'bg-light text-muted' : ''}`} onClick={() => changeViewResource(resource)}>
                                        <td className={`d-flex flex-row flex-md-column ${!resource.id ? 'fst-italic' : ''}`}>
                                            <div className="d-flex justify-content-between">
                                                <span className={`${resource.id === (viewResource || {}).id ? 'text-white' : ''}`}>{resource.propertyName}</span>
                                                {resource.id === (viewResource || {}).id && <div className="text-end text-white"><FontAwesomeIcon icon={faTimes} />&nbsp;Close</div>}
                                            </div>
                                            <div className="ms-1 text-end">
                                                <span style={{ fontSize: '0.75em' }} className={`fst-italic ${resource.id === (viewResource || {}).id ? 'text-white' : 'text-secondary'}`}>{resource.submitted ? FuzzyDate(resource.submitted) : 'Failed to Send'}</span>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </Col>
                <Col className={`${viewResource ? 'border-start h-100' : ''}`}>
                    {(viewResource) &&
                        <ViewInquiry key={`${(viewResource || {}).id || Math.random()}_${(viewResource || {}).loaded}`}
                            userId={CurrentUser.id}
                            {...{
                                config,
                                resource: viewResource,
                            }}
                            Save={(resource) => {
                                updateResource(resource);
                                viewResource.id = resource.id;
                            }}
                            Close={() => {
                                setViewResource(null);
                                setResourceIsDirty(false);
                            }}
                            IsDirty={(isDirty) => setResourceIsDirty(isDirty)}
                            ReloadResources={() => getResources(true)}
                        />
                    }
                </Col>
            </Row>
            {toasts}
        </div>
    );
};


// WEBPACK FOOTER //
// ./src/components/Inquiries/InquiryList.js